'use client';

import { ActiveLink } from '@/components/navigation/active-link';
import { Link } from 'react-router';
import { useLocation } from 'react-router';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard,
  Building2,
  Users,
  Shield,
  Menu,
  Brain,
  ArrowLeft,
  Gamepad2,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const platformItems = [
  { name: 'Overview', path: '/super-admin', icon: LayoutDashboard, exact: true },
  { name: 'Tenants', path: '/super-admin/organizations', icon: Building2 },
];

const accessItems = [
  { name: 'Usuarios', path: '/super-admin/users', icon: Users },
  { name: 'Super Admins', path: '/super-admin/admins', icon: Shield },
];

const productItems = [
  { name: 'Navegación', path: '/super-admin/navigation', icon: Menu },
  { name: 'Juegos', path: '/super-admin/games', icon: Gamepad2 },
  { name: 'Actividades', path: '/super-admin/activities', icon: Brain },
];

const aiItems = [
  { name: 'IA / OCR', path: '/super-admin/ai', icon: Sparkles },
];

const NavGroup = ({
  label,
  items,
  pathname,
}: {
  label: string;
  items: { name: string; path: string; icon: React.ComponentType<{ className?: string }>; exact?: boolean }[];
  pathname: string;
}) => (
  <SidebarGroup className="py-0">
    <SidebarGroupLabel className="mb-0.5 h-auto px-2 py-1">
      <div className="flex w-full items-center gap-2">
        <span className="font-display whitespace-nowrap text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
          {label}
        </span>
        <div className="h-px flex-1 bg-sidebar-border" />
      </div>
    </SidebarGroupLabel>
    <SidebarGroupContent>
      <SidebarMenu className="space-y-0.5">
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = item.exact
            ? pathname === item.path
            : pathname === item.path || pathname.startsWith(`${item.path}/`);

          return (
            <SidebarMenuItem key={item.path}>
              <SidebarMenuButton
                asChild
                isActive={isActive}
                tooltip={item.name}
                className={cn(
                  'relative h-8 rounded-none px-2 transition-colors duration-150',
                  'hover:bg-sidebar-accent',
                  isActive && 'nav-item-active',
                )}
              >
                <ActiveLink href={item.path} className="flex w-full items-center gap-2.5">
                  <Icon
                    className={cn(
                      'h-4 w-4 shrink-0',
                      isActive ? 'text-primary' : 'text-sidebar-foreground',
                    )}
                  />
                  <span
                    className={cn(
                      'text-sm',
                      isActive ? 'text-primary' : 'text-sidebar-foreground',
                    )}
                  >
                    {item.name}
                  </span>
                </ActiveLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroupContent>
  </SidebarGroup>
);

export const SuperAdminSidebar = () => {
  const location = useLocation();
  const pathname = location.pathname;

  return (
    <Sidebar variant="inset" className="border-r border-sidebar-border bg-sidebar">
      <SidebarHeader className="border-b border-sidebar-border px-4 py-3 bg-glow-radial">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-col">
            <span className="font-display text-sm font-bold leading-tight tracking-wide text-gradient-gold">
              Core Utils
            </span>
            <span className="text-[11px] leading-tight text-muted-foreground">
              Consola de Producto
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="h-7 w-7 shrink-0 p-0 text-muted-foreground hover:text-foreground"
            title="Volver al sistema"
          >
            <Link to="/dashboard">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </SidebarHeader>

      <SidebarContent className="space-y-2 px-2 py-3">
        <NavGroup label="Plataforma" items={platformItems} pathname={pathname} />
        <NavGroup label="Acceso" items={accessItems} pathname={pathname} />
        <NavGroup label="Producto" items={productItems} pathname={pathname} />
        <NavGroup label="IA" items={aiItems} pathname={pathname} />
      </SidebarContent>

      <SidebarFooter className="space-y-2 border-t border-sidebar-border px-4 py-3">
        <Button
          variant="outline"
          size="sm"
          asChild
          className="h-8 w-full justify-start gap-2 text-sm"
        >
          <Link to="/dashboard">
            <ArrowLeft className="h-3.5 w-3.5" />
            Volver al Sistema
          </Link>
        </Button>
        <div className="flex items-center justify-between">
          <span className="font-display text-[11px] font-semibold text-gradient-gold">Super Admin</span>
          <span className="text-[11px] text-muted-foreground/30" suppressHydrationWarning>© {new Date().getFullYear()}</span>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};
